import { useState, useEffect, useRef, useCallback } from "react"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Dialog, DialogContent, DialogBody } from "~/react/ui/dialog"
import { Skeleton } from "~/react/ui/skeleton"
import { Hint } from "~/react/ui/hint"
import { KeyValue, KeyValueItem, KeyValueTitle, KeyValueDescription } from "~/react/ui/key-value"
import { Alert } from "~/react/ui/alert"
import { Separator } from "~/react/ui/separator"
import { getCsrfToken, copyToClipboard } from "~/lib/utils"

type CredentialsData = {
  password?: string
  secret?: string
  admin_email?: string
  admin_password?: string
  admin_secret?: string
  admin_url?: string
}

type AuthCodeData = {
  code: string
  ttl: number
}

type CredentialsModalProps = {
  emailAccountId: number
  email: string
  isOpen: boolean
  onClose: () => void
}

export function CredentialsModal({ emailAccountId, email, isOpen, onClose }: CredentialsModalProps) {
  const [credentials, setCredentials] = useState<CredentialsData | null>(null)
  const [authCode, setAuthCode] = useState<string>("")
  const [authCodeTtl, setAuthCodeTtl] = useState<number>(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showAppPassword, setShowAppPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const fetchAuthCode = useCallback(async () => {
    try {
      const response = await fetch(`/email_accounts/${emailAccountId}/credentials/auth-code`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (!response.ok) {
        throw new Error("Failed to fetch authentication code")
      }

      const data: AuthCodeData = await response.json()
      setAuthCode(data.code)
      setAuthCodeTtl(data.ttl)
    } catch (err) {
      App.displayFlash(
        "error",
        err instanceof Error ? err.message : i18n.t("js.email_accounts_center.credentials.auth_code_error"),
      )
    }
  }, [emailAccountId])

  const fetchCredentials = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/email_accounts/${emailAccountId}/credentials`, {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })

      if (!response.ok) {
        throw new Error("Failed to fetch credentials")
      }

      const data = await response.json()
      if (Object.keys(data).length === 0) {
        setError(i18n.t("js.email_accounts_center.credentials.not_available"))
        return
      }

      setCredentials(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("js.email_accounts_center.credentials.fetch_error"))
    } finally {
      setLoading(false)
    }
  }, [emailAccountId])

  // Handle dialog open/close and initial fetch
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen) {
      dialog.showModal()
      if (emailAccountId) {
        fetchCredentials()
      }
    } else {
      dialog.close()
      // Reset state when modal closes
      setCredentials(null)
      setAuthCode("")
      setAuthCodeTtl(0)
      setShowPassword(false)
      setShowAppPassword(false)
      setError(null)
    }
  }, [isOpen, emailAccountId, fetchCredentials])

  // Handle native dialog close event (e.g. ESC key)
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleClose = () => {
      onClose()
    }

    dialog.addEventListener("close", handleClose)
    return () => dialog.removeEventListener("close", handleClose)
  }, [onClose])

  // Auto-fetch auth code when credentials are loaded and secret is available
  useEffect(() => {
    if (isOpen && credentials?.secret && !authCode && authCodeTtl === 0) {
      fetchAuthCode()
    }
  }, [isOpen, credentials?.secret, authCode, authCodeTtl, fetchAuthCode])

  // Auth code countdown
  useEffect(() => {
    if (authCodeTtl <= 0) return

    const interval = window.setInterval(() => {
      setAuthCodeTtl((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [authCodeTtl])

  // Auto-regenerate auth code when it expires
  useEffect(() => {
    if (isOpen && authCodeTtl === 0 && credentials?.secret && authCode) {
      fetchAuthCode()
    }
  }, [isOpen, authCodeTtl, credentials?.secret, authCode, fetchAuthCode])

  const handleOpenIntercom = () => {
    if ((window as any).Intercom) {
      ;(window as any).Intercom("showNewMessage")
    }
  }

  const handleClose = () => {
    dialogRef.current?.close()
  }

  if (!isOpen) {
    return null
  }

  return (
    <Dialog ref={dialogRef} id="credentials-modal">
      <DialogContent>
        <DialogBody>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h2 className="text-lg-medium mb-0" id="credentials-modal-label">
              {i18n.t("js.email_accounts_center.credentials.title")}
            </h2>
            <Button variant="ghost" size="xs" iconOnly type="button" onClick={handleClose} aria-label="Close">
              <Icon icon="xmark" variant="regular" size="sm" aria-hidden="true" />
            </Button>
          </div>
          {loading ? (
            <>
              <div className="text-xs mb-4">
                <Skeleton className="mb-2" size="freeform" style={{ height: "1.2rem", width: "100%" }} />
                <Skeleton className="mb-2" size="freeform" style={{ height: "1.2rem", width: "80%" }} />
                <Skeleton size="freeform" style={{ height: "1.2rem", width: "90%" }} />
              </div>
              <Separator className="my-4" />
              <KeyValue inline>
                <KeyValueItem className="mb-3">
                  <KeyValueTitle>
                    <Skeleton size="freeform" style={{ height: "1.4rem", width: "8rem" }} />
                  </KeyValueTitle>
                  <KeyValueDescription>
                    <Skeleton size="freeform" style={{ height: "1.4rem", width: "12rem" }} />
                  </KeyValueDescription>
                </KeyValueItem>
                <KeyValueItem className="mb-3">
                  <KeyValueTitle>
                    <Skeleton size="freeform" style={{ height: "1.4rem", width: "10rem" }} />
                  </KeyValueTitle>
                  <KeyValueDescription>
                    <Skeleton size="freeform" style={{ height: "1.4rem", width: "15rem" }} />
                  </KeyValueDescription>
                </KeyValueItem>
                <KeyValueItem>
                  <KeyValueTitle>
                    <Skeleton size="freeform" style={{ height: "1.4rem", width: "9rem" }} />
                  </KeyValueTitle>
                  <KeyValueDescription>
                    <Skeleton size="freeform" style={{ height: "1.4rem", width: "18rem" }} />
                  </KeyValueDescription>
                </KeyValueItem>
              </KeyValue>
            </>
          ) : error ? (
            <Alert variant="destructive">{error}</Alert>
          ) : credentials ? (
            <>
              <div className="text-xs">
                <p>{i18n.t("js.email_accounts_center.credentials.description")}</p>
                <p>{i18n.t("js.email_accounts_center.credentials.warning")}</p>
                <p>
                  {i18n.t("js.email_accounts_center.credentials.support_text")}{" "}
                  <Button
                    variant="linkGrey"
                    size="sm"
                    type="button"
                    className="open-intercom"
                    onClick={handleOpenIntercom}
                  >
                    {i18n.t("js.email_accounts_center.credentials.support_link")}
                  </Button>
                </p>
              </div>

              <Separator className="my-4" />

              <KeyValue inline>
                {credentials.admin_url && (
                  <KeyValueItem className="mb-3">
                    <KeyValueTitle>{i18n.t("js.email_accounts_center.credentials.admin_url")}:</KeyValueTitle>
                    <KeyValueDescription>
                      <Button
                        variant="linkGrey"
                        size="sm"
                        type="button"
                        onClick={() => window.open(credentials.admin_url, "_blank")}
                      >
                        {credentials.admin_url}
                      </Button>
                    </KeyValueDescription>
                  </KeyValueItem>
                )}

                <KeyValueItem className="mb-3">
                  <KeyValueTitle>{i18n.t("js.email_accounts_center.credentials.email_address")}:</KeyValueTitle>
                  <KeyValueDescription>{email}</KeyValueDescription>
                </KeyValueItem>

                {credentials.password && (
                  <KeyValueItem>
                    <KeyValueTitle>{i18n.t("js.email_accounts_center.credentials.password")}:</KeyValueTitle>
                    <KeyValueDescription className="d-flex align-items-center gap-1">
                      {showPassword ? credentials.password : "**********"}
                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={
                          showPassword
                            ? i18n.t("js.email_accounts_center.credentials.hide_password")
                            : i18n.t("js.email_accounts_center.credentials.show_password")
                        }
                      >
                        <Icon
                          icon={showPassword ? "eye-slash" : "eye"}
                          variant="regular"
                          size="xs"
                          aria-hidden="true"
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        type="button"
                        onClick={() => copyToClipboard(credentials.password || "")}
                        aria-label={i18n.t("js.email_accounts_center.credentials.copy_password")}
                      >
                        <Icon icon="copy" variant="regular" size="xs" aria-hidden="true" />
                      </Button>
                    </KeyValueDescription>
                  </KeyValueItem>
                )}

                {credentials.secret && (
                  <KeyValueItem>
                    <KeyValueTitle>{i18n.t("js.email_accounts_center.credentials.authentication_code")}:</KeyValueTitle>
                    <KeyValueDescription className="d-flex align-items-center gap-1">
                      {authCode || "—"}
                      {authCodeTtl > 0 && (
                        <Hint className="ml-auto">
                          ·{" "}
                          {i18n.t("js.email_accounts_center.credentials.reset_in", {
                            seconds: authCodeTtl,
                          })}
                        </Hint>
                      )}
                      {authCode && (
                        <Button
                          variant="ghost"
                          size="xs"
                          iconOnly
                          type="button"
                          onClick={() => copyToClipboard(authCode)}
                          aria-label={i18n.t("js.email_accounts_center.credentials.copy_auth_code")}
                        >
                          <Icon icon="copy" variant="regular" size="xs" aria-hidden="true" />
                        </Button>
                      )}
                    </KeyValueDescription>
                  </KeyValueItem>
                )}

                {credentials.admin_password && (
                  <KeyValueItem>
                    <KeyValueTitle>{i18n.t("js.email_accounts_center.credentials.app_password")}:</KeyValueTitle>
                    <KeyValueDescription className="d-flex align-items-center gap-1">
                      {showAppPassword ? credentials.admin_password : "**********"}
                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        type="button"
                        onClick={() => setShowAppPassword(!showAppPassword)}
                        aria-label={
                          showAppPassword
                            ? i18n.t("js.email_accounts_center.credentials.hide_app_password")
                            : i18n.t("js.email_accounts_center.credentials.show_app_password")
                        }
                      >
                        <Icon
                          icon={showAppPassword ? "eye-slash" : "eye"}
                          variant="regular"
                          size="xs"
                          aria-hidden="true"
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        type="button"
                        onClick={() => copyToClipboard(credentials.admin_password || "")}
                        aria-label={i18n.t("js.email_accounts_center.credentials.copy_app_password")}
                      >
                        <Icon icon="copy" variant="regular" size="xs" aria-hidden="true" />
                      </Button>
                    </KeyValueDescription>
                  </KeyValueItem>
                )}
              </KeyValue>
            </>
          ) : null}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
